import { Directive, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FilterTag } from '@zero/search/domain';
import { InstrumentType } from '@zero/shared/util-models';
import { AssetCounts, AssetSearchService, InstrumentSearchResults, ResultsEntity } from '@zero/shared/util-services';
import { Subject, Subscription, debounceTime } from 'rxjs';

@Directive()
export class TradableProductsBase implements OnInit, OnDestroy {
  public query?: string;
  public tags?: FilterTag[];
  public results?: ResultsEntity[];
  public hits?: number;
  public instrumentsLoaded = false;
  public shouldDisplaySavingsPlans = false;
  public shouldDisplayFractional = false;
  public query$ = new Subject<string>();
  public hasError = false;
  public readonly pageSize = 20;
  public currentPage = 1;
  public readonly instrumentType = InstrumentType;

  protected subscriptions: Subscription[] = [];

  constructor(
    protected route: ActivatedRoute,
    public service: AssetSearchService,
  ) {}

  public async ngOnInit(): Promise<void> {
    try {
      this.initTags();
      const [counts, { hits, results }] = await Promise.all([this.service.loadAssetCounts(), this.loadSearchResults()]);
      this.setCounts(counts);
      this.hits = hits;
      this.results = results;
      this.listenToTermChanges();
    } catch (e) {
      this.hasError = true;
    } finally {
      this.instrumentsLoaded = true;
    }
  }

  public ngOnDestroy(): void {
    if (this.subscriptions?.length) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
  }

  public toggleSavingsPlanTag = (): void => {
    this.shouldDisplaySavingsPlans = !this.shouldDisplaySavingsPlans;
    this.triggerSearch();
  };

  public toggleSavingsFractionalTag = (): void => {
    this.shouldDisplayFractional = !this.shouldDisplayFractional;
    this.triggerSearch();
  };

  public toggleFilterTag = (tag: FilterTag): void => {
    tag.isActive = !tag.isActive;
    this.triggerSearch();
  };

  public showMore = async (): Promise<void> => {
    if (this.hits < this.pageSize * this.currentPage) {
      return;
    }

    this.currentPage++;

    const { results, hits } = await this.loadSearchResults();

    this.hits = hits;
    this.results = [...this.results, ...results];
  };

  private loadSearchResults = (): Promise<InstrumentSearchResults> =>
    this.service.loadSearchResults({
      query: this.query,
      size: this.pageSize,
      from: this.pageSize * (this.currentPage - 1),
      types: this.tags.filter(({ isActive }) => isActive).map(({ alias }) => alias),
      sparplan: this.shouldDisplaySavingsPlans,
      fractional: this.shouldDisplayFractional,
    });

  private setCounts = (assetCounts: AssetCounts): void => {
    this.tags = this.tags.map((tag) => ({
      ...tag,
      count: assetCounts[tag.alias] ?? 0,
    }));
  };

  private initTags = (): void => {
    this.tags = [
      new FilterTag('A', 'AKTIE'),
      new FilterTag('E', 'ETF'),
      new FilterTag('C', 'ETC'),
      new FilterTag('F', 'FONDS'),
      new FilterTag('D', 'DERIVAT'),
      new FilterTag('K', 'KRYPTO'),
    ];

    const { type, sp } = this.route.snapshot.queryParams;
    this.shouldDisplaySavingsPlans = sp === 'true';

    if (type) {
      type.split('-').forEach((value: string) => {
        const tag = this.tags.find(({ name }) => name === value);
        if (tag) {
          tag.isActive = true;
        }
      });
    }
  };

  private listenToTermChanges = (): void => {
    this.subscriptions.push(
      this.query$.pipe(debounceTime(200)).subscribe((searchTerm) => {
        this.query = searchTerm;
        this.triggerSearch();
      }),
    );
  };

  private triggerSearch = async (): Promise<void> => {
    this.currentPage = 1;
    const { results, hits } = await this.loadSearchResults();
    this.hits = hits;
    this.results = results;
  };
}
